"use client";

import React, { useEffect, useRef, useState } from "react";
import { getFivePostByKeyword, getPostDataByUrl } from "@/app/api/post/postAPI";
import Link from "next/link";
import TableOfContent from "@/components/common/post/TableOfContent";
import { Skeleton } from "primereact/skeleton";
import "@/assets/jodit-css/jodit-post.css";
import dynamic from "next/dynamic";

const JoditEditor = dynamic(() => import("jodit-react"), { ssr: false });

const cleanHtmlContent = (raw) => {
  if (!raw) return "";

  let html = raw
    .replace(/^"+|"+$/g, "")
    .replace(/\\n/g, "")
    .replace(/\\t/g, "")
    .replace(/\\"/g, '"');

  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    `<img src="$2" alt="$1" style="max-width:100%;height:auto" />`
  );

  return html.trim();
};

function Post2({ url }) {
  const [postTitle, setPostTitle] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [headingList, setHeadingList] = useState(null);
  const [fiveNewestPost, setFiveNewestPost] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(true);

  // ✅ CACHE BÀI LIÊN QUAN
  const relatedCacheRef = useRef({});

  const setDocumentMetadata = (seo, post) => {
    if (!seo && !post) return;

    document.title = seo?.metaTitle || post?.name || "Con Rùa Biển";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute(
      "content",
      seo?.metaDescription || post?.description || ""
    );
  };

  const addTableToPostDetail = (dataTable, dataContent) => {
    let safeContent = cleanHtmlContent(dataContent);
    let idTables = dataTable.map((t) => `banggia=${t?.id}`);
    return insertTablePrices(safeContent, dataTable, idTables);
  };

  const insertTablePrices = (dataContent, tablePrices, idTables) => {
    let safeContent = cleanHtmlContent(dataContent);

    for (const id of idTables) {
      const extractedNumber = parseInt(id.match(/\d+/)?.[0]);

      for (const tablePrice of tablePrices) {
        if (tablePrice?.id === extractedNumber) {
          const header = `
            <h2 class="ftwp-heading">
              <strong>${tablePrice?.titleTable}</strong>
            </h2>
          `;

          safeContent = safeContent.replace(
            new RegExp(id + "(?=<)", "g"),
            header + `<div>${JSON.parse(tablePrice?.contentTable)}</div>`
          );
        }
      }
    }

    let index = 0;
    safeContent = safeContent.replace(/(<h\d)/g, (match) => {
      const res = match + ` data-header="${index}"`;
      index += 1;
      return res;
    });

    return safeContent;
  };

  // ✅ LOAD SONG SONG + CACHE BÀI LIÊN QUAN
  const loadPostAndRelated = async (url) => {
    try {
      setLoadingRelated(true);

      const postRes = await getPostDataByUrl(url);

      setDocumentMetadata(postRes?.metaSeo, postRes?.postDto);
      setPostTitle(postRes?.postDto?.name);

      let rawContent = postRes?.postDto?.content || "";
      let content = cleanHtmlContent(rawContent);

      if (postRes?.tablePriceList == null) {
        let index = 0;
        setPostDetail(
          content.replace(/(<h\d)/g, (m) => m + ` data-header="${index++}"`)
        );
      } else {
        setPostDetail(
          addTableToPostDetail(postRes?.tablePriceList, content)
        );
      }

      const keyword =
        postRes?.metaSeo?.metaKeywords || postRes?.postDto?.name;

      // ✅ DÙNG CACHE
      if (relatedCacheRef.current[keyword]) {
        setFiveNewestPost(relatedCacheRef.current[keyword]);
        setLoadingRelated(false);
        return;
      }

      const relatedRes = await getFivePostByKeyword(keyword);

      if (relatedRes?.data?.data?.postDtos && postRes?.postDto?.id) {
        relatedRes.data.data.postDtos =
          relatedRes.data.data.postDtos.filter(
            (p) => p.id !== postRes.postDto.id
          );
      }

      if (
        relatedRes?.data?.data?.shippedProjectPostDtos &&
        postRes?.postDto?.id
      ) {
        relatedRes.data.data.shippedProjectPostDtos =
          relatedRes.data.data.shippedProjectPostDtos.filter(
            (p) => p.id !== postRes.postDto.id
          );
      }

      // ✅ LƯU CACHE
      relatedCacheRef.current[keyword] = relatedRes;
      setFiveNewestPost(relatedRes);
    } catch (err) {
      console.error("Load post & related error:", err);
    } finally {
      setLoadingRelated(false);
    }
  };

  useEffect(() => {
    if (postDetail !== null) {
      setTimeout(() => {
        const list = [];
        const headerList = document.querySelectorAll("[data-header]");
        let id = 0;

        headerList.forEach((dataKey) => {
          list.push({
            id: id++,
            text: dataKey.innerText,
            tag: dataKey.nodeName,
          });
        });

        setHeadingList(list);
      }, 800);
    }
  }, [postDetail]);

  useEffect(() => {
    if (!url) return;

    loadPostAndRelated(url);
    setFiveNewestPost(null);
  }, [url]);

  return (
    <>
      {postTitle == null ? (
        <div className="container-fluid block-content" style={{ marginTop: 20 }}>
          <Skeleton width="90%" height="300px" />
        </div>
      ) : (
        <div className="container-fluid block-content" style={{ marginTop: 20 }}>
          <div className="row main-grid">
            <div className="col-sm-9 posts">
              <h1 className="text-center">{postTitle.toUpperCase()}</h1>

              {headingList && <TableOfContent data={headingList} />}

              <div style={{ marginBottom: "20%" }}>
                <JoditEditor
                  value={postDetail}
                  className="jodit-editor"
                  config={{
                    toolbar: false,
                    readonly: true,
                    toolbarInline: false,
                  }}
                />
              </div>
            </div>

            {/* ✅ SIDEBAR CACHE + PRELOAD */}
            <div
              className="col-sm-3"
              style={{ position: "sticky", top: 0, marginTop: -53 }}
            >
              <div className="sidebar-container">
                <h4 style={{ color: "#C8322b", fontWeight: 700 }}>
                  Bài viết liên quan
                </h4>

                {loadingRelated ? (
                  <Skeleton width="100%" height="150px" />
                ) : (
                  <ul className="blog-cats">
                    {fiveNewestPost?.data?.data?.postDtos?.map((data) => (
                      <li key={data.id}>
                        <Link href={data.urlName}>
                          <h5>{data.name}</h5>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                <h4 style={{ color: "#C8322b", fontWeight: 700, marginTop: 25 }}>
                  Dự án đã vận chuyển
                </h4>

                {loadingRelated ? (
                  <Skeleton width="100%" height="150px" />
                ) : (
                  <ul className="blog-cats">
                    {fiveNewestPost?.data?.data?.shippedProjectPostDtos?.map(
                      (data) => (
                        <li key={data.id}>
                          <Link href={data.urlName}>
                            <h5>{data.name}</h5>
                          </Link>
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Post2;
